/** 依赖注入作用域类型 */
export type Scope = "singleton" | "transient";

/**
 * 构造器类型定义
 *
 * 必须使用 any[] 作为构造函数参数类型来兼容 Vue 的 mixin 要求
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ClassConstructor<T = any> = new (...args: any[]) => T;

/**
 * 注入元信息
 *
 * 存储类的依赖注入配置信息
 */
export interface InjectableMeta<T extends object> {
  /** 作用域：singleton（单例）或 transient（瞬态） */
  scope: Scope;
  /** 目标类 */
  target: ClassConstructor<T>;
  /** 依赖列表 */
  deps?: ClassConstructor[];
}

// 容器存储
export const injectableMap = new Map<
  ClassConstructor<object>,
  InjectableMeta<object>
>();
const singletonMap = new Map<ClassConstructor<object>, object>();

// 循环依赖检测栈
const constructionStack = new Set<ClassConstructor<object>>();

/**
 * 注册类为可注入
 *
 * @param cls - 要注册的类
 * @param scope - 作用域（singleton 或 transient）
 * @param deps - 可选，依赖的类列表
 *
 * @example
 * ```typescript
 * markInjectable(MyService, 'singleton', [ConfigService]);
 * ```
 */
export function markInjectable<T extends object>(
  cls: ClassConstructor<T>,
  scope: Scope,
  deps?: ClassConstructor[],
): void {
  injectableMap.set(cls, { scope, target: cls, deps });
}

/**
 * 获取类实例（DI 核心入口）
 *
 * 此函数会：
 * 1. 检查类是否已注册为可注入
 * 2. 对于单例，返回缓存的实例
 * 3. 检测循环依赖
 * 4. 递归解析并注入依赖
 * 5. 实例化类
 * 6. 缓存单例实例
 *
 * @param cls - 要获取实例的类
 * @returns 类实例
 * @throws 如果类未注册或检测到循环依赖
 *
 * @example
 * ```typescript
 * const service = getInstance(MyService);
 * service.doSomething();
 * ```
 */
export function getInstance<T extends object>(cls: ClassConstructor<T>): T {
  const meta = injectableMap.get(cls as ClassConstructor<object>);
  if (!meta) {
    throw new Error(`${cls.name} 未使用 @Injectable() 注册`);
  }

  // 单例复用逻辑
  if (meta.scope === "singleton") {
    const existing = singletonMap.get(cls);
    if (existing) {
      return existing as T;
    }
  }

  // 循环依赖检测
  if (constructionStack.has(cls as ClassConstructor<object>)) {
    const path = Array.from(constructionStack).map((c) => c.name);
    path.push(cls.name);
    throw new Error(`检测到循环依赖: ${path.join(" -> ")}`);
  }
  constructionStack.add(cls as ClassConstructor<object>);

  // 移除 ProxyClass 类型约束
  const proxyCls = meta.target as ClassConstructor<T> & {
    _allowConstruct?: boolean;
  };

  // 设置构造标志
  proxyCls._allowConstruct = true;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  const deps = meta.deps?.map((dep) => getInstance(dep)) ?? [];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const instance = new proxyCls(...deps);
  proxyCls._allowConstruct = false;

  // 单例缓存
  if (meta.scope === "singleton") {
    singletonMap.set(cls, instance);
  }

  constructionStack.delete(cls as ClassConstructor<object>);
  return instance;
}

/**
 * 释放单例实例
 *
 * 从单例缓存中移除实例，如果实例实现了 Destroyable 接口，则调用其 onDestroy() 方法。
 *
 * @param cls - 要释放的类
 *
 * @example
 * ```typescript
 * destroyInstance(MyService);
 * ```
 */
export function destroyInstance<T extends object>(
  cls: ClassConstructor<T>,
): void {
  if (!singletonMap.has(cls as ClassConstructor<object>)) {
    return;
  }

  const instance = singletonMap.get(cls as ClassConstructor<object>);
  singletonMap.delete(cls as ClassConstructor<object>);

  if (instance !== undefined && isDestroyable(instance)) {
    instance.onDestroy();
  }
}

/**
 * 可销毁接口
 *
 * 实现此接口的类在被销毁时会自动调用 onDestroy() 方法进行清理。
 */
export interface Destroyable {
  onDestroy(): void;
}

function isDestroyable(obj: unknown): obj is Destroyable {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "onDestroy" in obj &&
    typeof (obj as Destroyable).onDestroy === "function"
  );
}

/**
 * 依赖树可视化（调试用）
 *
 * 生成类的依赖树字符串表示，用于调试和理解依赖关系。
 *
 * @param cls - 要可视化的类
 * @returns 依赖树的字符串表示
 *
 * @example
 * ```typescript
 * const tree = getDependencyTree(MyService);
 * console.log(tree);
 * // 输出：
 * // MyService
 * //   ConfigService
 * //   UserService
 * ```
 */
export function getDependencyTree(cls: ClassConstructor): string {
  const tree: string[] = [];
  const visited = new Set<ClassConstructor>();

  function traverse(current: ClassConstructor, depth = 0): void {
    if (visited.has(current)) {
      return;
    }
    visited.add(current);

    const indent = "  ".repeat(depth);
    tree.push(`${indent}${current.name}`);

    // 实际项目中应使用反射获取依赖关系
    // 此处为简化实现
    const meta = injectableMap.get(current);
    if (meta) {
      // 模拟依赖遍历
      traverse(meta.target as ClassConstructor, depth + 1);
    }
  }

  traverse(cls);
  return tree.join("\n");
}
